import React, { useState, useEffect } from 'react';
import './CreateAccountBroker.scss';

import { StepBarContent } from '../StepBar/StepBar';
import { FooterContent } from '../ResumeFooter/ResumeFooter';
import { OptionDropdownInner } from '../../atoms/OptionDropdown/OptionDropdown';
import { TextInput } from '../../atoms/TextInput/TextInput';
import PasswordInput from '../../atoms/TextInput/PasswordInput';
import { AddressCaption } from '../PreApprovalForm/PreApprovalForm';
import { AddressCaptionInner } from '../PreApprovalForm/PreApprovalForm';
import { InputAutocompleteAddress } from '../../atoms/TextInput/TextInput';

import { formatPhoneNumber } from '../PreApprovalForm/PreApprovalForm';
import { SPECIAL_CHARACTERS_REGEX } from '../../../utils/constants';
import { useCheckBorrowerEmailExists } from '../../../services/borrowers';

const options = [
  {
    value: "client",
    label: "My client",
    disabled: false,
    className: undefined
  }
]

export const CreateAccountBroker = (props) => {
  /* Props */
  const { onSubmission, values } = props;

  /* User Data */
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [password, setPassword] = useState('')
  const [passwordVerification, setPasswordVerification] = useState('')


  const { checkEmail, emailExists, loading: emailCheckLoading } = useCheckBorrowerEmailExists();

  const [borrowerFirstName, setBorrowerFirstName] = useState("")
  const [borrowerLastName, setBorrowerLastName] = useState("")
  const [investmentAddress, setInvestmentAddress] = useState("")

  const createOnChange = (fun) => (e) => fun(e.target.value);


  const [emailCheckTimeout, setEmailCheckTimeout] = useState(null);
  
  const checkEmailExists = async (emailValue) => {
    if (!emailValue || !emailValue.includes('@')) {
      setEmailValidationState({
        isChecking: false,
        hasBeenChecked: false,
        exists: false
      });
      return;
    }

    setEmailValidationState(prev => ({ ...prev, isChecking: true }));
    
    try {
      await checkEmail({ variables: { email: emailValue } });
      // The emailExists value will be updated by the useEffect below
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailValidationState({
        isChecking: false,
        hasBeenChecked: false,
        exists: false
      });
    }
  };

  const debouncedEmailCheck = (emailValue) => {
    if (emailCheckTimeout) {
      clearTimeout(emailCheckTimeout);
    }
    
    const timeout = setTimeout(() => {
      checkEmailExists(emailValue);
    }, 500); // 500ms debounce
    
    setEmailCheckTimeout(timeout);
  };

  /* State - Step */
  const [step, setStep] = useState(0)

  const firstStepCompleted = (
    true && firstName && lastName && email && phoneNumber && password && passwordVerification
  )


  const setSelectedStep = (newStep) => {
    if (step === 0 && !firstStepCompleted) {
      return
    } else {
      setStep(newStep)
    }
  }

  const nextStep = () => {
    setSelectedStep(step + 1)
  }

  /* Markup */
  const firstStepOnClick = () => {
    nextStep()
  }

  const stepBarMarkup = (
    <StepBarContent
      selectedStep={step}
      setSelectedStep={setSelectedStep}
      options={["Broker Information", "Borrower Information"]}
    />
  )

  const [errors, setErrors] = useState({});
  const [borrowerErrors, setBorrowerErrors] = useState({});
  const [emailValidationState, setEmailValidationState] = useState({
    isChecking: false,
    hasBeenChecked: false,
    exists: false
  });

  const brokerData = {
    firstName,
    lastName,
    email,
    phoneNumber,
    password,
    passwordVerification
  }

  const isFormBrokerComplete = () => {
    return Object.values(brokerData).every(value => value !== undefined && value !== '');
  }

  const validatePasswordRequirements = (password) => {
    const requirements = [
      { label: "Lowercase letter", validator: (pwd) => /[a-z]/.test(pwd) },
      { label: "Uppercase letter", validator: (pwd) => /[A-Z]/.test(pwd) },
      { label: "Symbol", validator: (pwd) => SPECIAL_CHARACTERS_REGEX.test(pwd) },
      { label: "8 characters", validator: (pwd) => pwd.length >= 8 }
    ];

    return requirements.map(req => ({
      ...req,
      met: req.validator(password)
    }));
  };

  const isPasswordValid = (password) => {
    const requirements = validatePasswordRequirements(password);
    return requirements.every(req => req.met);
  };

  const validateInput = (label, value) => {
    switch (label) {
      case "First name":
      case "Last name":
      case "Borrower First Name":
      case "Borrower Last Name":
        return value.trim() === "" ? "This field is required" : null;
      case "Email address":
        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(value)) return "Invalid email address";
        if (emailValidationState.isChecking) return "Checking email availability...";
        if (emailValidationState.hasBeenChecked && emailValidationState.exists) {
          return "This email is already registered";
        }
        return null;
      case "Phone number":
        if (value.trim() === "") return "This field is required";
        const digits = value.replace(/\D/g, '');
        if (digits.length < 10) return "Phone number must have 10 digits";
        return null;
      case "Password":
        if (value.length === 0) return "Password is required";
        if (!isPasswordValid(value)) return "Password does not meet requirements";
        return null;
      case "Confirm Password":
        if (!password || !value) return "Please confirm your password";
        if (value !== password) return "Passwords do not match";
        return null;
      case "Investment Address":
        return value.trim() === "" ? "Investment address is required" : null;
      default:
        return null;
    }
  };

  const checkErrors = () => {
    const newErrors = {
      firstName: validateInput("First name", firstName),
      lastName: validateInput("Last name", lastName),
      email: validateInput("Email address", email),
      phoneNumber: validateInput("Phone number", phoneNumber),
      password: validateInput("Password", password),
      passwordConfirmation: validateInput("Confirm Password", passwordVerification)
    };
    setErrors(newErrors);
    return newErrors
  }

  const checkBorrowerErrors = () => {
    const newBorrowerErrors = {
      borrowerFirstName: validateInput("Borrower First Name", borrowerFirstName),
      borrowerLastName: validateInput("Borrower Last Name", borrowerLastName),
      investmentAddress: validateInput("Investment Address", investmentAddress)
    };
    setBorrowerErrors(newBorrowerErrors);
    return newBorrowerErrors;
  }

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      checkErrors();
    }
  }, [firstName, lastName, email, phoneNumber, password, passwordVerification])

  useEffect(() => {
    if (Object.keys(borrowerErrors).length > 0) {
      checkBorrowerErrors();
    }
  }, [borrowerFirstName, borrowerLastName, investmentAddress])

  useEffect(() => {
    if (email && email.includes('@')) {
      debouncedEmailCheck(email);
    } else {
      setEmailValidationState({
        isChecking: false,
        hasBeenChecked: false,
        exists: false
      });
    }
    
    return () => {
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
    };
  }, [email]);

  useEffect(() => {
    if (emailValidationState.isChecking && !emailCheckLoading) {
      setEmailValidationState(prev => ({
        ...prev,
        isChecking: false,
        hasBeenChecked: true,
        exists: emailExists
      }));
    }
  }, [emailExists, emailCheckLoading, emailValidationState.isChecking]);

  const handleContinue = () => {
    const errors = checkErrors();
    const hasErrors = Object.values(errors).some(error => error !== null);
    const isEmailValid = !emailValidationState.isChecking && 
                        (!emailValidationState.hasBeenChecked || !emailValidationState.exists);
    
    if (isFormBrokerComplete() && !hasErrors && isEmailValid) {
      nextStep()
    }
  }

  const firstStepMarkup = (
    <React.Fragment>
      <div className='inner-container'>
        {stepBarMarkup}
        <h1 className='title'>
          Enter your info to offer loans to your real estate investor clients
        </h1>

        <div className='wide-input-container'>
          <TextInput label={"First name"} value={firstName} onChange={(e) => { setFirstName(e) }} error={errors.firstName} />
          <TextInput label={"Last name"} value={lastName} onChange={(e) => setLastName(e)} error={errors.lastName} />
          <TextInput label={"Email address"} value={email} onChange={(e) => setEmail(e)} error={errors.email} />
          <TextInput label={"Phone number"} value={phoneNumber} onChange={(e) => setPhoneNumber(formatPhoneNumber(e))} error={errors.phoneNumber} />
          <div className='password-container'>
            <PasswordInput
              label={"Password"}
              value={password}
              onChange={(e) => setPassword(e)}
              error={errors.password}
              showRequirements={true}
            />
            <PasswordInput
              label={"Confirm Password"}
              value={passwordVerification}
              onChange={(e) => setPasswordVerification(e)}
              error={errors.passwordConfirmation}
              showRequirements={false}
            />
          </div>
        </div>
      </div>

      <div className='resume-container'>
        <FooterContent values={values} oneLine={true} isSpan={true} isBrokerMode={true} isBrokerResume={true} />
      </div>

      <div className='loan-options-button' onClick={handleContinue}>
        Go to borrower info
      </div>
      <p className='caption'>
        We will NEVER sell your info.<br></br>
        By providing your phone and email, you agree to receive text messages from Constitution Lending.
      </p>
    </React.Fragment>
  )

  const isBorrowerFormComplete = () => {
    return borrowerFirstName && borrowerLastName && investmentAddress;
  }

  const secondStepOnClick = () => {
    const borrowerErrors = checkBorrowerErrors();
    const hasBorrowerErrors = Object.values(borrowerErrors).some(error => error !== null);
    
    if (isBorrowerFormComplete() && !hasBorrowerErrors) {
      const userData = {
        broker: {
          firstName, lastName, email, phoneNumber, password
        },
        borrower: {
          firstName: borrowerFirstName,
          lastName: borrowerLastName,
          investmentAddress: investmentAddress
        }
      }
      onSubmission(userData)
    } else {
      // Force validation to show errors if form is incomplete
      checkBorrowerErrors();
    }
  }

  const secondStepMarkup = (
    <React.Fragment>
      <div className='padding-container'>
        {stepBarMarkup}
        <h1 className='title'>
          Enter borrower info to get a term sheet and pre-approval letter
        </h1>

        <p className='description'>
          <OptionDropdownInner
            defaultValue={options[0]}
            options={options}
            customWidth={"200px"}
            isBrokerMode={true}
          /> is the borrower
        </p>

        <div className='input-container'>
          <TextInput label='First Name' value={borrowerFirstName} onChange={(e) => setBorrowerFirstName(e)} error={borrowerErrors.borrowerFirstName} />
          <TextInput label='Last Name' value={borrowerLastName} onChange={(e) => setBorrowerLastName(e)} error={borrowerErrors.borrowerLastName} />
        </div>
      </div>

      <div className='resume-container first'>
        <FooterContent values={values} oneLine={true} isSpan={true} isBrokerFirst={true} />
      </div>

      <div className='padding-container'>
        <div className='property'>
          <InputAutocompleteAddress
            className='broker'
            onSelect={({ formatted_address }) => {
              const addressWithoutCountry = formatted_address.replace(/, [^,]*$/, "");
              setInvestmentAddress(addressWithoutCountry);
            }}
            error={borrowerErrors.investmentAddress}
          />
        </div>
        <AddressCaption />
      </div>

      <div className='resume-container last'>
        <FooterContent values={values} oneLine={true} isSpan={true} isBrokerLast={true} />
      </div>

      <div className='loan-options-button' onClick={secondStepOnClick}>
        Get a term sheet for your client
      </div>
      <p className='caption'>
        We will NEVER sell your info.<br></br>
        By providing your phone and email, you agree to receive text messages from Constitution Lending.
      </p>
    </React.Fragment>
  )

  /* Component */
  return (
    <div className='create-account-broker broker'>
      <div className='info'>
        {
          step === 0
            ?
            firstStepMarkup
            :
            secondStepMarkup
        }
      </div>
    </div>
  );
}
